import { useEffect, useState } from "react";
import { X, Loader2, History } from "lucide-react";
import { fetchRevisions } from "@/lib/contentOps/api";
import type { ArticleRevision } from "@/lib/contentOps/types";

interface Props { articleId: string; onClose: () => void }

const RevisionsDrawer = ({ articleId, onClose }: Props) => {
  const [revisions, setRevisions] = useState<ArticleRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    fetchRevisions(articleId).then(setRevisions).finally(() => setLoading(false));
  }, [articleId]);

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" style={{ background: "hsl(0 0% 0% / 0.6)" }} />
      <div className="w-full max-w-md h-full overflow-y-auto border-l p-5"
        style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 14%)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "hsl(0 0% 90%)" }}>
            <History size={14} /> Revisions
          </h3>
          <button onClick={onClose} style={{ color: "hsl(0 0% 60%)" }}><X size={16} /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-32" style={{ color: "hsl(0 0% 50%)" }}>
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : revisions.length === 0 ? (
          <p className="text-xs text-center py-8" style={{ color: "hsl(0 0% 50%)" }}>No revisions yet.</p>
        ) : (
          <ul className="space-y-2">
            {revisions.map((r) => (
              <li key={r.id} className="rounded-lg border p-3" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium" style={{ color: "hsl(207 68% 65%)" }}>{r.revision_type}</span>
                  <span style={{ color: "hsl(0 0% 45%)" }}>{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="text-[11px] mt-1" style={{ color: "hsl(0 0% 55%)" }}>
                  {r.section_key && <>section: {r.section_key} · </>}
                  {r.ai_model ?? "human"}{r.ai_confidence ? ` · ${Math.round(r.ai_confidence * 100)}%` : ""}
                </div>
                <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  className="text-[11px] mt-2" style={{ color: "hsl(207 68% 60%)" }}>
                  {expanded === r.id ? "Hide" : "View snapshot"}
                </button>
                {expanded === r.id && (
                  <pre className="mt-2 text-[10px] overflow-x-auto rounded p-2 max-h-64"
                    style={{ background: "hsl(0 0% 3%)", color: "hsl(0 0% 70%)" }}>
                    {JSON.stringify(r.snapshot, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RevisionsDrawer;
